import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { applyFormErrorsRecursively } from '@gsa/afp-shared-form-utils';
import { useCurrentUser } from '@gsa/afp-shared-ui-utils';
import {
  Spinner,
  useModal,
  Alert,
  Button,
  Checkbox,
} from '@gsa/afp-component-library';
import ReadonlyOptionGroup from '../../../../vehicle-referral/replace-vehicle/steps/review-submit/readonly-option-group';
import {
  updateSelectedOption,
  removeSelectedOption,
  changeAdditionalsStep,
  setSupplementaryInformation,
} from '../../../../../reducers/additionals-reducer';
import { UserRoles, UserTypes } from '../../../../../constants';
import {
  useGetAddOptions,
  useReturnAdditionalRequest,
  useFillVehicleRequest,
} from '../../../../../requests/additionals-request';
import {
  ADDITIONAL_REQUEST_STATUS,
  isExistInRoles,
  validateAdditionalsButtons,
} from '../../../utils/additional-utils';
import { ADDITIONAL_VEHICLE_STEPS } from '../../create-vehicle-request-steps';
import AdditionalRequestActionButtons from '../../../components/additionals-request-buttons/additionals-request-buttons';
import SupplementaryInfo from './supplementary-info/supplementary-info';
import useCreateAdditionalDraft from '../../../components/create-draft/use-create-additional-draft';
import ReturnModal from '../../../components/return-modal/return-modal';
import FillExistingVehicleModal from '../../../components/fill-vehicle-modal/fill-existing-vehicle-modal';
import FundingValidModal from '../../../components/funding-valid-modal/funding-valid-modal';
import CreateAdditionalRequisition from '../../../components/create-requisition/create-requisition';
import useBackToList from '../../../utils/use-back-to-list';
import './additionals-review-submit.scss';

export const EDITABLE_STATUSES = [
  ADDITIONAL_REQUEST_STATUS.DRAFT,
  ADDITIONAL_REQUEST_STATUS.RETURNED,
];

export const FILL_IN_VEHICLE_STATUS = [
  ADDITIONAL_REQUEST_STATUS.PENDING_GSA_APPROVAL,
  ADDITIONAL_REQUEST_STATUS.APPROVED_AT_FSR,
];

export default function AdditionalsReviewSubmit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const {
    selectedOptions,
    selectedStandardItem,
    sinNotAvailable,
    status,
    additionalsDraftData,
    selectedReasonForRequest,
  } = useSelector((state) => state.additionalsReducer);
  const approvalLevel = additionalsDraftData?.approvalLevel;
  const { requestId } = useParams();
  const { backToList } = useBackToList();
  const [createAdditionalRequest] = useCreateAdditionalDraft();
  const [returnAdditionalRequest, { loading: returnLoading }] =
    useReturnAdditionalRequest();
  const [updateFillVehicleRequest, { loading: fillInLoading }] =
    useFillVehicleRequest();
  const [unavailableOptions, setUnavailableOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const { isOpen, closeModal, openModal } = useModal();
  const fillVehicleModal = useModal();

  // Roles
  const { currentUser } = useCurrentUser();
  const roles = currentUser?.roles || [];
  const isEmployee = currentUser?.userType.id === UserTypes.GSA_EMPLOYEE;
  // Internal/GSA users
  const isOrderingAdmin = isExistInRoles(roles, UserRoles.ORDERING_ADMIN);
  const isFsr = isExistInRoles(roles, UserRoles.FSR);
  const isSiteAdmin = isExistInRoles(roles, UserRoles.SITE_ADMIN);
  const reOpenUsers = isOrderingAdmin || isFsr || isSiteAdmin;

  const isDelinquencyCheckPendingGSA =
    additionalsDraftData?.deliquencyCheck &&
    additionalsDraftData?.requestStatusCode ===
      ADDITIONAL_REQUEST_STATUS.PENDING_GSA_APPROVAL;

  // Customer type users
  const isCustomerUser =
    isExistInRoles(roles, UserRoles.CUSTOMER_ADMINISTRATOR) ||
    isExistInRoles(roles, UserRoles.CUSTOMER_FLEET_MANAGER);
  const agencyLevelApprover =
    isCustomerUser && isExistInRoles(roles, UserRoles.AGENCY_APPROVER);
  const bureauLevelApprover =
    isCustomerUser && isExistInRoles(roles, UserRoles.BUREAU_APPROVER);
  const designatedLevelUsers = agencyLevelApprover || bureauLevelApprover;
  const editUsers = isCustomerUser || isOrderingAdmin || isSiteAdmin;

  const canEdit =
    status === ADDITIONAL_REQUEST_STATUS.DRAFT ||
    (status === ADDITIONAL_REQUEST_STATUS.RETURNED && editUsers);
  const canReturnRequest = additionalsDraftData?.canReturnRequest;

  // if we are loading the review/submit from the db, we need to get the full additional option data
  const selectedOptionsFromDb =
    selectedOptions.length > 0
      ? selectedOptions.some((selectedOption) => !selectedOption.option)
      : false;
  const additionalOptions = useGetAddOptions(selectedStandardItem?.id, {
    skip: !selectedOptionsFromDb,
    raw: true,
  });
  useEffect(() => {
    if (selectedOptionsFromDb && !additionalOptions.loading) {
      if (additionalOptions.data.length === 0) {
        console.warn(
          'there are options in the state, but none coming from the api.',
        );
      }

      for (let selected of selectedOptions) {
        const option = additionalOptions.data.find(
          (option) => selected.equipmentCode === option.equipmentCode.code,
        );
        if (!option) {
          dispatch(removeSelectedOption(selected));
          setUnavailableOptions((prev) => [...prev, selected]);
        } else {
          dispatch(updateSelectedOption({ option, comment: selected.comment }));
        }
      }
    }
  }, [additionalOptions.data, additionalOptions.loading]);

  if (additionalOptions.loading || selectedOptionsFromDb) {
    return <Spinner size="large" />;
  }

  const handleSupplementaryInformationSubmit = (finished) => {
    handleSubmit(false, null);
  };

  if (additionalOptions.loading || selectedOptionsFromDb) {
    return <Spinner size="large" />;
  }

  const groupedByCategory = {};
  for (let item of selectedOptions) {
    if (!groupedByCategory[item.option.equipmentCode.categoryCode.title]) {
      groupedByCategory[item.option.equipmentCode.categoryCode.title] = [];
    }

    groupedByCategory[item.option.equipmentCode.categoryCode.title].push(item);
  }

  // sort the keys alphabetically
  const sortedKeys = Object.keys(groupedByCategory).sort();
  const sortedGroupedByCategory = {};

  for (let key of sortedKeys) {
    sortedGroupedByCategory[key] = groupedByCategory[key].sort((a, b) => {
      if (a.option.equipmentCode.code < b.option.equipmentCode.code) return -1;
      if (a.option.equipmentCode.code > b.option.equipmentCode.code) return 1;
      return 0;
    });
  }

  const handleSubmit =
    (autoNavigate = false, navigateStep) =>
    () => {
      const formData = formRef.current.getValues();
      const reviewState = {
        missionMakeModel:
          formData.requireMakeModel === 'yes' ? formData.makeModel : null,
        specialInstructions: formData.specialInstructions,
        acknowledge: formData.acknowledge,
      };
      dispatch(setSupplementaryInformation(reviewState));
      const payload = {
        additionalRequestId: requestId,
        requestStatusCode: 'DRAFT',
        missionMakeModel:
          formData?.requireMakeModel === 'yes' ? formData?.makeModel : null,
        acknowledgedSubmit: formData?.acknowledge,
        specificInstructions: formData?.specialInstructions,
      };
      createAdditionalRequest(payload).then(({ data }) => {
        if (data) {
          dispatch(changeAdditionalsStep(navigateStep));
          window.scrollTo(0, 0);
        }
      });
      if (!navigateStep) {
        navigate('/lease/additionals');
        window.scrollTo(0, 0);
      }
    };

  const returnRequest = (data) => {
    returnAdditionalRequest({
      variables: {
        additionalRequestId: requestId,
        returnReason: data?.comment,
      },
    }).then(() => {
      closeModal();
      backToList({
        submitted: true,
        status: ADDITIONAL_REQUEST_STATUS.RETURNED,
        additionalRequestNumber: additionalsDraftData?.additionalRequestNumber,
      });
    });
    return;
  };

  const handleFillVehicle = (data) => {
    updateFillVehicleRequest({
      variables: {
        additionalRequestId: requestId,
        fillInVehicleDetailsComment: data?.comment,
      },
    }).then(() => {
      closeModal();
      backToList({
        submitted: true,
        status: ADDITIONAL_REQUEST_STATUS.FILLED_INVENTORY,
        additionalRequestNumber: additionalsDraftData?.additionalRequestNumber,
      });
    });
    return;
  };
  const handleSave = async () => {
    const data = formRef.current.getValues();
    const { setError, clearErrors } = formRef.current;
    const values = formRef.current.getValues();
    const { errors } = await formRef.current.validate(values, {}, {}, true);

    if (Object.keys(errors).length > 0) {
      for (const key of Object.keys(errors)) {
        applyFormErrorsRecursively(key, errors, setError, clearErrors);
      }
      return;
    }
    const payload = {
      additionalRequestId: requestId,
      missionMakeModel:
        data?.requireMakeModel === 'yes' ? data?.makeModel : null,
      acknowledgedSubmit: data?.acknowledge,
      requestStatusCode: 'DRAFT',
      specificInstructions: data?.specialInstructions,
    };
    createAdditionalRequest(payload).then(({ data }) => {
      if (data) {
        backToList();
        window.scrollTo(0, 0);
      }
    });
  };

  let submitButtonType = 'next';
  let submitButtonLabel = '';
  switch (approvalLevel) {
    case ADDITIONAL_REQUEST_STATUS.PENDING_GSA_APPROVAL:
      submitButtonLabel = 'Submit for approval';
      break;
    case ADDITIONAL_REQUEST_STATUS.OFFICE_APPROVAL:
      submitButtonLabel = 'Submit to office reviewer';
      break;
    case ADDITIONAL_REQUEST_STATUS.BUREAU_APPROVAL:
      submitButtonLabel = 'Submit to bureau reviewer';
      break;
    case ADDITIONAL_REQUEST_STATUS.AGENCY_APPROVAL:
      submitButtonLabel = 'Submit to agency reviewer';
      break;
    default:
      submitButtonLabel = 'Submit for approval';
      break;
  }

  const handleViewRequisition = () => {
    window.open(`/lease/${additionalsDraftData?.requisitionId}`, '_blank');
  };

  const isReqCreated =
    additionalsDraftData?.requisitionId &&
    status === ADDITIONAL_REQUEST_STATUS.REQUISITION_CREATED &&
    isEmployee;

  if (returnLoading || fillInLoading) {
    return <Spinner size="large" />;
  }
  return (
    <div className="replacement-request-review-submit">
      <div>
        <div className="margin-top-5 margin-bottom-5">
          <h3>Reason for request</h3>
          <div>{selectedReasonForRequest}</div>
        </div>

        <div className="review-acknowledge padding-5 margin-bottom-1">
          <Checkbox checked={true} disabled />
          <div className="acknowledge-section margin-left-5">
            By requesting a leased vehicle through GSA Fleet, I acknowledge that
            this is a <strong>long-term vehicle need </strong> intended for
            regular, ongoing operational use. For short-term vehicle needs, I
            can utilize the <strong>Short-Term Rental Program</strong> instead.
          </div>
        </div>
      </div>
      <div>
        <div className="section-header">
          <h3>Selected options</h3>
          <p>
            In accordance with FMR 102-34.50, optional equipment selections
            should be limited to what is mission essential. Not all selected
            options are guaranteed. Options selected may affect the final lease
            rate.
          </p>
        </div>

        {unavailableOptions.length > 0 && (
          <Alert className="margin-bottom-2" type="info">
            Options that were previously selected are no longer available:
            <br />
            {unavailableOptions
              .map((option) => option.equipmentCode)
              .join(', ')}
          </Alert>
        )}

        <div className="selected-options">
          {Object.keys(sortedGroupedByCategory).length === 0 ? (
            <Alert>No selected options</Alert>
          ) : (
            Object.keys(sortedGroupedByCategory).map((category) => (
              <ReadonlyOptionGroup
                key={category}
                name={category}
                options={sortedGroupedByCategory[category]}
              />
            ))
          )}
        </div>
      </div>

      <SupplementaryInfo
        formRef={formRef}
        onSubmit={handleSupplementaryInformationSubmit}
        isEditable={canEdit}
      />

      {sinNotAvailable && (
        <div>
          <Alert type="error" className="show-error-alert">
            <b>There are no makes and models available for this SIN.</b>
            <div> You can: </div>
            <ul>
              <li>
                Wait until the contract has been awarded and create the
                requisition
              </li>
              <li>
                Re-open the request and return it to the customer to select a
                different SIN
              </li>
              <li>Override SIN selection</li>
            </ul>
          </Alert>
        </div>
      )}

      <div className="review-btns">
        <AdditionalRequestActionButtons
          prevButton={{
            label: 'Previous',
            leftIcon: { name: 'arrow_back' },
            onClick: handleSubmit(
              true,
              ADDITIONAL_VEHICLE_STEPS.SELECT_OPTIONS,
            ),
          }}
          cancelButton={{
            label: 'Cancel request',
            disabled: false,
          }}
          saveButton={{
            onClick: () => handleSave(),
          }}
          returnRequest={{
            onClick: openModal,
          }}
          submitButton={{
            label: submitButtonLabel,
            type: submitButtonType,
            nextFormId: 'garage',
          }}
          returnToSearch={{
            leftIcon: { name: 'arrow_back' },
            onClick: () => {
              navigate('/lease/additionals');
            },
          }}
          fillExistVehicle={{
            onClick: fillVehicleModal.openModal,
          }}
          nextFormId="supplementary-information-form"
          hideButtons={[
            (!EDITABLE_STATUSES.includes(status) || !canEdit) && 'previous',
            (status === ADDITIONAL_REQUEST_STATUS.DRAFT || canEdit) &&
              'returnToSearch',
            (!EDITABLE_STATUSES.includes(status) || !canEdit) && 'cancel',
            (!EDITABLE_STATUSES.includes(status) || !canEdit) && 'save',
            !canReturnRequest && 'returnRequest',
            !approvalLevel &&
              (!EDITABLE_STATUSES.includes(status) || !canEdit) &&
              'submit',
            (isDelinquencyCheckPendingGSA ||
              !FILL_IN_VEHICLE_STATUS.includes(status) ||
              !reOpenUsers) &&
              'fillExistVehicle',
          ]}
        />

        {isReqCreated && (
          <Button
            variant="primary"
            onClick={() => {
              handleViewRequisition();
            }}
            label="View requisition"
            type="button"
            className="margin-left-2"
          />
        )}
      </div>

      {additionalsDraftData?.fundingStatus === 'FUNDED' &&
        status === ADDITIONAL_REQUEST_STATUS.APPROVED_AT_FSR &&
        reOpenUsers && (
          <div className="bg-primary-lightest padding-3 margin-top-3">
            <CreateAdditionalRequisition />
          </div>
        )}

      <ReturnModal
        isOpen={isOpen}
        handleCancel={closeModal}
        accountNumber={additionalsDraftData?.additionalRequestNumber}
        handleReturn={returnRequest}
      />

      <FillExistingVehicleModal
        isOpen={fillVehicleModal.isOpen}
        handleCancel={fillVehicleModal.closeModal}
        handleVehicleSubmit={handleFillVehicle}
      />
    </div>
  );
}
